import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import ModuleHeader from "../components/ModuleHeader";
import { Table } from "antd";
import { uamApi } from "@/api/uamApi";
import { rem2px, REM_CONFIG } from "@/utils/fontSize";
// ==========================
// 核心组件
// ==========================
const WarningReminders = ({ className = "", initDate = "2025-09", }) => {
    const ROW_HEIGHT = rem2px(REM_CONFIG.rowHeight);
    const [isLoading, setIsLoading] = useState(true);
    const [tableHeight, setTableHeight] = useState("auto");
    const [isScrolling, setIsScrolling] = useState(true);
    const tableContainerRef = useRef(null);
    const scrollTimer = useRef(null);
    const [tableData, setTableData] = useState([]);
    const columns = [
        {
            title: "序号",
            key: "序号",
            width: "15%",
            render: (_, __, index) => `${index + 1}`,
        },
        {
            title: "项目",
            dataIndex: "projectName",
            key: "projectName",
            width: "70%",
            ellipsis: true,
        },
        {
            title: "数量",
            dataIndex: "qty",
            key: "projectType",
            width: "15%",
            ellipsis: true,
        },
    ];
    useEffect(() => {
        getData();
    }, []);
    const getData = async () => {
        try {
            setIsLoading(true);
            const res = await uamApi.totalAssetByProject();
            setTableData(res.data || []);
        }
        catch (error) {
            console.error("获取服务项目数据失败:", error);
            setTableData([]);
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        const calculateTableHeight = () => {
            if (!tableContainerRef.current)
                return;
            const container = tableContainerRef.current;
            const parent = container.parentElement;
            if (!parent)
                return;
            const header = container.previousElementSibling;
            const headerHeight = header?.offsetHeight || 0;
            const unitText = container.querySelector("p");
            const unitTextHeight = unitText ? unitText.offsetHeight : 0;
            const topSpacing = rem2px(REM_CONFIG.spacing.top);
            const totalTopHeight = headerHeight + unitTextHeight + topSpacing;
            const parentHeight = parent.offsetHeight;
            const availableHeight = parentHeight - totalTopHeight;
            setTableHeight(Math.floor(availableHeight));
        };
        calculateTableHeight();
        const resizeObserver = new ResizeObserver(calculateTableHeight);
        requestAnimationFrame(() => {
            if (tableContainerRef.current) {
                resizeObserver.observe(tableContainerRef.current);
            }
        });
        return () => resizeObserver.disconnect();
    }, [tableData, ROW_HEIGHT]);
    useEffect(() => {
        if (scrollTimer.current)
            clearInterval(scrollTimer.current);
        if (tableHeight === "auto" || !isScrolling || tableData.length === 0)
            return;
        const scrollContainer = tableContainerRef.current?.querySelector(".ant-table-body");
        if (!scrollContainer)
            return;
        scrollTimer.current = setInterval(() => {
            const { scrollTop, clientHeight, scrollHeight } = scrollContainer;
            const scrollStep = REM_CONFIG.spacing.scrollStep;
            let newScrollTop = scrollTop + scrollStep;
            if (newScrollTop + clientHeight >= scrollHeight - 1)
                newScrollTop = 0;
            scrollContainer.scrollTop = newScrollTop;
        }, 50);
        return () => {
            if (scrollTimer.current)
                clearInterval(scrollTimer.current);
        };
    }, [tableHeight, isScrolling, tableData.length]);
    const handleMouseEnter = () => setIsScrolling(false);
    const handleMouseLeave = () => setIsScrolling(true);
    return (_jsxs("div", { className: `relative bg-gradient-to-t from-[rgba(7,31,75,0.5)] to-[rgba(6,32,66,0.2)] flex flex-col ${className}`, children: [_jsx(ModuleHeader, { title: "\u79D1\u7814\u9879\u76EE\u5206\u5E03" }), _jsxs("div", { ref: tableContainerRef, className: "custom-table-container flex-1 w-full h-full min-h-0", style: {
                    minHeight: 0,
                    width: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    paddingLeft: `${REM_CONFIG.spacing.paddingX}rem`,
                    paddingRight: `${REM_CONFIG.spacing.paddingX}rem`,
                }, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [_jsx(Table, { columns: columns, dataSource: tableData, pagination: false, scroll: { y: tableHeight, x: "100%" }, style: {
                            width: "100%",
                            tableLayout: "fixed",
                            borderCollapse: "collapse",
                        }, onRow: (record, index) => ({
                            style: {
                                backgroundColor: index % 2 === 0 ? "transparent" : "#062654",
                                height: `${ROW_HEIGHT}px`,
                            },
                        }) }), _jsx("style", { children: `
          /* 表格基础重置 */
.custom-table-container .ant-table,
.custom-table-container .ant-table table {
  background-color: transparent !important;
  color: #FFFFFF !important;
  border: none !important;
  border-collapse: collapse !important;
  border-spacing: 0 !important;
}

/* 表头样式 */
.custom-table-container .ant-table-thead > tr > th {
  background-color: #062a58 !important;
  color: #00ffff !important;
  border: none !important; 
  border-right: 1px solid #062a58 !important; 
  border-left: 1px solid #062a58 !important;
  font-weight: 600;
  line-height: ${ROW_HEIGHT}px !important;
  padding: 0 ${REM_CONFIG.spacing.paddingX}rem !important;
  font-size: ${REM_CONFIG.fontSize.small} !important;
}
.custom-table-container .ant-table-thead > tr > th:first-child { border-left: none !important; }
.custom-table-container .ant-table-thead > tr > th:last-child { border-right: none !important; }

/* 表格内容样式 */
.custom-table-container .ant-table-tbody > tr > td {
  color: #FFFFFF !important;
  border: none !important;
  line-height: ${ROW_HEIGHT}px !important;
  padding: 0 ${REM_CONFIG.spacing.paddingX}rem !important;
  font-size: ${REM_CONFIG.fontSize.small} !important;
}
.custom-table-container .ant-table-tbody > tr:hover > td {
  background-color: #1A446E !important;
}

/* 滚动容器 */
.custom-table-container .ant-table-body {
  scrollbar-width: none;
  -ms-overflow-style: none;
  border: none !important;
  overflow-y: auto !important;
  height: auto !important;
  min-height: 0 !important;
  padding-top: 0 !important;
  margin-top: 0 !important;
}
.custom-table-container .ant-table-body::-webkit-scrollbar { display: none; }

/* 清除 tbody 间距 */
.custom-table-container .ant-table-tbody {
  margin-top: 0 !important;
  padding-top: 0 !important;
  border-top: none !important;
  transform: translateY(0) !important;
}

/* 隐藏测量空行 关键 */
.custom-table-container .ant-table-measure-row {
  display: none !important;
  height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  border: none !important;
}

/* 无数据：只隐藏占位提示，保留表头 */
.custom-table-container .ant-table-placeholder {
  display: none !important;
  height: 0 !important;
}

/* 清除多余分隔线 */
.custom-table-container .ant-table-container { border: none !important; }
.custom-table-container .ant-table-thead::after,
.custom-table-container .ant-table-cell::before { display: none !important; }
          ` })] })] }));
};
export default WarningReminders;
